import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export function TrackingSettingsCard() {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/tracking/settings").then((r) => setT(r.data)); }, []);
  if (!t) return null;
  const set = (k, v) => setT((p) => ({ ...p, [k]: v }));
  const toggleDay = (d) => set("days", t.days.includes(d) ? t.days.filter((x) => x !== d) : [...t.days, d].sort());
  const save = async () => {
    setBusy(true);
    try {
      const { enabled, days, start, end, interval_min, consent_text, office_address, office_lat, office_lng, office_radius_m } = t;
      setT((await api.put("/admin/tracking/settings", { enabled, days, start, end, interval_min: Number(interval_min) || 5, consent_text,
        office_address, office_lat: Number(office_lat), office_lng: Number(office_lng), office_radius_m: Number(office_radius_m) || 100 })).data);
      toast.success("Geolocalizzazione salvata");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const here = () => navigator.geolocation?.getCurrentPosition(
    ({ coords }) => setT((p) => ({ ...p, office_lat: +coords.latitude.toFixed(6), office_lng: +coords.longitude.toFixed(6) })),
    () => toast.error("Posizione non disponibile"), { enableHighAccuracy: true, timeout: 15000 });
  return (
    <section className="rounded-2xl border bg-white p-5 sm:p-6" data-testid="tracking-settings-card">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-lg font-semibold">Geolocalizzazione tecnici</h2>
        <Switch checked={t.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="tracking-enabled-switch" />
      </div>
      <p className="mb-3 text-xs text-slate-500">La posizione viene registrata solo mentre l'app è aperta, nei giorni e orari indicati e solo per i tecnici senza assenze approvate. Lo storico si cancella dopo 30 giorni.</p>
      <div className="flex flex-wrap gap-2">
        {DAYS.map((d, i) => (
          <button key={d} type="button" data-active={t.days.includes(i)} onClick={() => toggleDay(i)} data-testid={`tracking-day-${i}`}
            className="choice rounded-full border bg-white px-3.5 py-1.5 text-sm">{d}</button>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="space-y-2"><Label>Dalle</Label><Input type="time" value={t.start} onChange={(e) => set("start", e.target.value)} data-testid="tracking-start" /></div>
        <div className="space-y-2"><Label>Alle</Label><Input type="time" value={t.end} onChange={(e) => set("end", e.target.value)} data-testid="tracking-end" /></div>
        <div className="space-y-2"><Label>Ogni (min)</Label><Input type="number" min="1" max="60" value={t.interval_min} onChange={(e) => set("interval_min", e.target.value)} data-testid="tracking-interval" /></div>
      </div>
      <div className="mt-4 space-y-2">
        <Label>Testo del consenso mostrato ai tecnici</Label>
        <Textarea rows={6} value={t.consent_text} onChange={(e) => set("consent_text", e.target.value)} data-testid="tracking-consent-textarea" />
        <p className="text-xs text-slate-500">Se modifichi il testo, ogni tecnico dovrà accettarlo di nuovo.</p>
      </div>
      <div className="mt-5 space-y-2 border-t pt-5" data-testid="tracking-office">
        <p className="text-sm font-semibold">Sede per la timbratura con QR Code</p>
        <Input value={t.office_address} onChange={(e) => set("office_address", e.target.value)} placeholder="Indirizzo sede" data-testid="tracking-office-address" />
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1"><Label>Latitudine</Label><Input type="number" step="0.000001" value={t.office_lat} onChange={(e) => set("office_lat", e.target.value)} data-testid="tracking-office-lat" /></div>
          <div className="space-y-1"><Label>Longitudine</Label><Input type="number" step="0.000001" value={t.office_lng} onChange={(e) => set("office_lng", e.target.value)} data-testid="tracking-office-lng" /></div>
          <div className="space-y-1"><Label>Raggio (m)</Label><Input type="number" min="20" value={t.office_radius_m} onChange={(e) => set("office_radius_m", e.target.value)} data-testid="tracking-office-radius" /></div>
        </div>
        <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={here} data-testid="tracking-office-here">Usa la mia posizione attuale (stando in sede)</Button>
      </div>
      <Button onClick={save} disabled={busy} className="mt-4 rounded-full" data-testid="tracking-save-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva geolocalizzazione</Button>
    </section>
  );
}
